import { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { FilePlus2, FileSearch, CheckSquare, Square } from "lucide-react";
import MissionControlHeader from "../components/dashboard/MissionControlHeader";
import QuoteFilters from "../components/dashboard/QuoteFilters";
import QuoteBulkToolbar from "../components/dashboard/QuoteBulkToolbar";
import QuoteRow, { EmptyState } from "../components/dashboard/QuoteRow";
import { isQuoteNew } from "../lib/analysisJobs";

const INITIAL_FILTERS = { status: "all", customer_id: "all", date_from: "", date_to: "", q: "", source: "all", detected_language: "all" };

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") params[k] = v;
      });
      const { data } = await api.get("/quotes", { params });
      setQuotes(data || []);
      setSelectedIds((prev) => {
        const visible = new Set((data || []).map((d) => d.id));
        const next = new Set();
        prev.forEach((id) => { if (visible.has(id)) next.add(id); });
        return next;
      });
    } catch (_e) { setQuotes([]); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  // A background analysis finished while the user is here: the new quote
  // appears on top of the list without a manual refresh.
  useEffect(() => {
    window.addEventListener("mp-quote-ready", load);
    return () => window.removeEventListener("mp-quote-ready", load);
  }, [load]);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {}); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("dashboard.deleteConfirm"))) return;
    await api.delete(`/quotes/${id}`);
    load();
  };

  const visibleIds = useMemo(() => quotes.map((q) => q.id), [quotes]);
  const allSelected = selectedIds.size > 0 && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  const toggleOne = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (fn, errKey) => {
    setBulkBusy(true); setBulkError("");
    try {
      await fn(Array.from(selectedIds));
      clearSelection();
      await load();
    } catch (e) {
      setBulkError(e.response?.data?.detail || t(errKey));
    }
    setBulkBusy(false);
  };

  const bulkChangeStatus = (status) => runBulk(
    (ids) => api.post("/quotes/bulk/status", { ids, status }),
    "dashboard.errChangeStatus",
  );
  const bulkDelete = () => runBulk(
    (ids) => api.post("/quotes/bulk/delete", { ids }),
    "dashboard.errBulkDelete",
  );
  const bulkExportZip = async () => {
    setBulkBusy(true); setBulkError("");
    try {
      const res = await api.post("/quotes/bulk/export-zip", { ids: Array.from(selectedIds) }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `machinepilot-preventivi-${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setBulkError(e.response?.data?.detail || t("dashboard.errZip"));
    }
    setBulkBusy(false);
  };

  const resetFilters = () => setFilters(INITIAL_FILTERS);
  const activeCount = Object.entries(filters).filter(([_, v]) => v && v !== "all").length;

  return (
    <AppLayout>
      <div className="px-8 py-8" data-testid="dashboard">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-cyan-400 mb-2">// {t("dashboard.console")}</div>
            <h1 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">{t("dashboard.title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/import-time-study" className="px-4 py-2.5 rounded-sm flex items-center gap-2 font-mono text-xs tracking-wider uppercase bg-slate-900 text-slate-300 border border-slate-700 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors" data-testid="import-ts-cta">
              <FileSearch size={14}/> {t("dashboard.importTs")}
            </Link>
            <Link to="/new-quote" className="px-4 py-2.5 rounded-sm flex items-center gap-2 font-mono text-xs tracking-wider uppercase bg-[#FF4500] text-white hover:bg-[#E03D00] transition-colors shadow-[0_0_18px_rgba(255,69,0,0.3)]" data-testid="new-quote-cta">
              <FilePlus2 size={14}/> {t("dashboard.newFromDrawing")}
            </Link>
          </div>
        </div>

        <MissionControlHeader/>

        <QuoteFilters
          t={t} filters={filters} setFilters={setFilters} customers={customers}
          showFilters={showFilters} setShowFilters={setShowFilters}
          resetFilters={resetFilters} activeCount={activeCount}
        />

        <div className="tech-card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/60 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {quotes.length > 0 && (
                <button onClick={toggleAll} className="text-slate-400 hover:text-blue-400" data-testid="select-all-quotes" title={allSelected ? t("dashboard.deselectAll") : t("dashboard.selectAll")}>
                  {allSelected || someSelected ? <CheckSquare size={18} className={`text-blue-400 ${someSelected ? "opacity-60" : ""}`}/> : <Square size={18}/>}
                </button>
              )}
              <div className="label-tech">{t("dashboard.history")}</div>
            </div>
            <div className="text-xs text-slate-500 font-mono-data">{t("dashboard.recordCount", { n: quotes.length })}</div>
          </div>

          {selectedIds.size > 0 && (
            <QuoteBulkToolbar
              t={t} selectedCount={selectedIds.size} busy={bulkBusy} error={bulkError}
              onChangeStatus={bulkChangeStatus}
              onExportZip={bulkExportZip}
              onDelete={bulkDelete}
              onClear={clearSelection}
            />
          )}

          {loading ? (
            <div className="p-12 text-center text-slate-500">{t("common.loading")}</div>
          ) : quotes.length === 0 ? (
            activeCount > 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="empty-filtered">
                <div className="text-sm">{t("dashboard.emptyFiltered")}</div>
                <button onClick={resetFilters} className="mt-3 text-blue-400 hover:text-blue-300 text-sm">{t("dashboard.resetFilters")}</button>
              </div>
            ) : <EmptyState t={t} />
          ) : (
            <div className="divide-y divide-slate-800">
              {quotes.map((q) => (
                <QuoteRow
                  key={q.id} q={q}
                  isNew={isQuoteNew(q.id)}
                  selected={selectedIds.has(q.id)}
                  onToggle={toggleOne}
                  onRemove={remove}
                  t={t} locale={i18n.language}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
